import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { getOrganizerCompetitions, archiveCompetition } from '../services/organizer-api.js';
import Loading from '../components/common/Loading.jsx';
import ErrorMessage from '../components/common/ErrorMessage.jsx';
import Button from '../components/common/Button.jsx';
import './OrganiserDashboard.css';

/**
 * OrganiserDashboard
 * Main dashboard for competition organisers
 * Shows competition overview and management actions
 */
export default function OrganiserDashboard() {
  const navigate = useNavigate();
  const { user, isOrganizer } = useAuth();
  const [competitions, setCompetitions] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    draft: 0,
    completed: 0,
    archived: 0,
  });

  useEffect(() => {
    if (isOrganizer) {
      loadCompetitions();
    }
  }, [isOrganizer]);

  const loadCompetitions = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getOrganizerCompetitions();
      const comps = data.competitions || [];
      setCompetitions(comps);

      // Calculate stats (excluding archived from total count)
      const archivedCount = comps.filter(c => c.archived).length;
      const activeComps = comps.filter(c => !c.archived);
      
      setStats({
        total: activeComps.length,
        active: activeComps.filter(c => c.status === 'active').length,
        draft: activeComps.filter(c => c.status === 'draft').length,
        completed: activeComps.filter(c => c.status === 'completed').length,
        archived: archivedCount,
      });
    } catch (err) {
      console.error('Failed to load competitions:', err);
      setError(err.message || 'Failed to load competitions');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (competitionId, currentlyArchived) => {
    try {
      await archiveCompetition(competitionId, !currentlyArchived);
      // Reload competitions after archiving
      loadCompetitions();
    } catch (err) {
      console.error('Failed to archive competition:', err);
      setError(err.message || 'Failed to archive competition');
    }
  };

  if (!user || !isOrganizer) {
    return (
      <div className="organiser-dashboard">
        <div className="auth-required">
          <h2>Access Denied</h2>
          <p>This page is only accessible to competition organisers.</p>
          <Button onClick={() => navigate('/')}>Return Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="organiser-dashboard">
      <div className="dashboard-header">
        <h1>Organiser Dashboard</h1>
        <p className="welcome-message">Welcome back, {user.name || user.email}</p>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <Link to="/organizer/competitions/create" className="action-card primary">
          <div className="action-icon">➕</div>
          <h3>Create Competition</h3>
          <p>Set up a new league or tournament</p>
        </Link>
        <Link to="/competitions" className="action-card">
          <div className="action-icon">🏆</div>
          <h3>View All Competitions</h3>
          <p>Browse and manage existing competitions</p>
        </Link>
        <a href="#competitions-section" className="action-card">
          <div className="action-icon">📊</div>
          <h3>Competition Stats</h3>
          <p>View overview and statistics</p>
        </a>
      </div>

      {/* Statistics Overview */}
      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Active Competitions</div>
        </div>
        <div className="stat-card active">
          <div className="stat-value">{stats.active}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-card draft">
          <div className="stat-value">{stats.draft}</div>
          <div className="stat-label">Draft</div>
        </div>
        <div className="stat-card completed">
          <div className="stat-value">{stats.completed}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card archived">
          <div className="stat-value">{stats.archived}</div>
          <div className="stat-label">Archived</div>
        </div>
      </div>

      {/* Competitions List */}
      <section id="competitions-section" className="competitions-section">
        <div className="section-header">
          <h2>Your Competitions</h2>
          <div className="header-actions">
            <label className="filter-toggle">
              <input 
                type="checkbox" 
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show Archived
            </label>
            <Link to="/organizer/competitions/create" className="btn btn-primary">
              + Create New
            </Link>
          </div>
        </div>

        {loading && <Loading />}

        {error && (
          <ErrorMessage 
            message={error} 
            onRetry={loadCompetitions}
          />
        )}

        {!loading && !error && competitions.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">🏆</div>
            <h3>No Competitions Yet</h3>
            <p>Get started by creating your first competition.</p>
            <Link to="/organizer/competitions/create">
              <Button variant="primary">Create Competition</Button>
            </Link>
          </div>
        )}

        {!loading && !error && competitions.length > 0 && (
          <div className="competitions-grid">
            {competitions
              .filter(comp => showArchived ? comp.archived : !comp.archived)
              .map((competition) => (
              <div key={competition.competitionId} className={`competition-card ${competition.archived ? 'archived' : ''}`}>
                <div className="competition-header">
                  <h3>
                    {competition.name}
                    {competition.archived && <span className="archived-label"> (Archived)</span>}
                  </h3>
                  <span className={`status-badge ${competition.status}`}>
                    {competition.status}
                  </span>
                </div>
                <div className="competition-details">
                  <div className="detail-row">
                    <span className="label">Type:</span>
                    <span className="value">{competition.competitionType}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Divisions:</span>
                    <span className="value">
                      {competition.divisionIds?.length || competition.divisions?.length || 0}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Start Date:</span>
                    <span className="value">
                      {new Date(competition.startDate).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                </div>
                <div className="competition-actions">
                  <Link 
                    to={`/competitions/${competition.competitionId}`}
                    className="btn btn-secondary btn-sm"
                  >
                    View
                  </Link>
                  <Link 
                    to={`/organizer/competitions/${competition.competitionId}/edit`}
                    className="btn btn-outline btn-sm"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleArchive(competition.competitionId, competition.archived)}
                    className="btn btn-outline btn-sm"
                    title={competition.archived ? 'Unarchive' : 'Archive'}
                  >
                    {competition.archived ? '📤 Unarchive' : '📦 Archive'}
                  </button>
                  {competition.competitionType === 'knockout' && (
                    <Link
                      to={`/competitions/${competition.competitionId}/bracket`}
                      className="btn btn-outline btn-sm"
                    >
                      🏆 Bracket
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
